'use client';

/**
 * MIGRATED VERSION - Login Form
 *
 * Changes from original:
 * ✅ Uses useAdminForm hook (replaces ~40 lines of form handling)
 * ✅ Uses ErrorAlert component (replaces ~8 lines)
 * ✅ Improved validation with type safety
 *
 * Total reduction: ~48 lines
 * Original: 200 lines → Migrated: ~152 lines
 */

import { supabase } from '@/lib/supabase/client';
import Link from 'next/link';
import { errorMsg } from '@/lib/utils/error';
import { setUserLocale } from '@/i18n/actions';
import { type Locale, locales } from '@/i18n/locales';

// UI Components — clean 2026: no Card wrapper, no icons inside inputs,
// no "security features" pills. Form sits directly on the AuthShell.
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { toast } from 'sonner';
import { useAdminForm } from '@/hooks/useAdminForm';
import { ErrorAlert } from '@/components/admin/shared';
import { AuthNotice } from './AuthShell';

interface LoginFormProps {
  tenantId?: string;
  isUnifiedAdmin?: boolean;
  initialEmail?: string;
}

interface LoginFormData {
  email: string;
  password: string;
}

export default function LoginForm({ tenantId, isUnifiedAdmin = false, initialEmail }: LoginFormProps) {
  const tAuth = useTranslations('admin.auth');

  // Sign-in method tabs — password (default) vs magic-link. Magic-link
  // uses Supabase OTP (signInWithOtp) and bypasses the password step
  // entirely; the user receives an email with a click-to-sign-in link.
  // Fortune-500 pattern: Linear, Vercel, and Notion all surface both
  // options on the same page so users self-select.
  const [mode, setMode] = useState<'password' | 'magic-link'>('password');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkBusy, setMagicLinkBusy] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState(initialEmail || '');
  const [magicLinkError, setMagicLinkError] = useState<string | null>(null);

  const [magicLinkNotRegistered, setMagicLinkNotRegistered] = useState(false);

  // Google OAuth — same flow as RegisterForm (Supabase provider with
  // offline access + consent prompt so we can refresh the token later
  // when we wire up Google Calendar sync). Sits above the mode tabs:
  // ~40% of B2B users prefer the one-tap option, hiding it costs
  // conversion measurably.
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);

  const handleGoogleLogin = async () => {
    setOauthError(null);
    setOauthLoading(true);
    const { error: oauthErr } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback?next=/admin`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
    if (oauthErr) {
      setOauthError(errorMsg(oauthErr) || tAuth('errorFailed'));
      setOauthLoading(false);
    }
    // Otherwise the browser redirects — keep the loading state.
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!magicLinkEmail) {
      setMagicLinkError(tAuth('errorMissingFields'));
      return;
    }
    setMagicLinkBusy(true);
    setMagicLinkError(null);
    setMagicLinkNotRegistered(false);
    try {
      // Route through our own `/api/auth/magic-link` endpoint instead of
      // calling `supabase.auth.signInWithOtp` directly. This swaps
      // Supabase's default auth-email for our Brand-Blue Resend template.
      const res = await fetch('/api/auth/magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: magicLinkEmail, tenantId }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 404 && data?.code === 'NOT_REGISTERED') {
        // Surface the missing-account state explicitly so the user can
        // click straight through to register without re-typing the email.
        setMagicLinkNotRegistered(true);
        return;
      }
      if (!res.ok) throw new Error(tAuth('errorFailed'));

      setMagicLinkSent(true);
      toast.success(tAuth('magicLinkSentToast'));
    } catch (err) {
      setMagicLinkError(errorMsg(err) || tAuth('errorFailed'));
    } finally {
      setMagicLinkBusy(false);
    }
  };

  // ✅ NEW: Use useAdminForm hook instead of manual state management
  const {
    formData,
    loading: isLoading,
    error,
    handleChange,
    handleSubmit,
    clearError,
  } = useAdminForm<LoginFormData>({
    initialData: {
      email: initialEmail || '',
      password: '',
    },
    validate: (data) => {
      if (!data.email || !data.password) {
        return tAuth('errorMissingFields');
      }
      return null;
    },
    onSubmit: async (data) => {
      // signInWithPassword has NO native timeout — if Supabase Auth or the
      // network stalls, the user stares at a spinner indefinitely (the "login
      // takes ~20s, no feedback" bug). Race it against a hard deadline.
      const SIGN_IN_TIMEOUT_MS = 15000;
      const signIn = supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(
          () =>
            reject(
              new Error('Anmeldung dauert zu lange — bitte Verbindung prüfen und erneut versuchen.'),
            ),
          SIGN_IN_TIMEOUT_MS,
        ),
      );

      const { error: loginError, data: authData } = await Promise.race([signIn, timeout]);

      if (loginError) {
        throw new Error(errorMsg(loginError) || tAuth('errorFailed'));
      }

      // Preload the tenant's locale — but NON-BLOCKING. It only sets a cookie
      // for the next render (the redirect below uses the current document lang,
      // not this), so it must never sit on the login critical path. Previously
      // these two sequential queries + setUserLocale ran BEFORE the redirect.
      if (authData?.user?.id) {
        const userId = authData.user.id;
        void (async () => {
          try {
            const { data: roleData } = await supabase
              .from('tenant_user_roles')
              .select('tenant_id')
              .eq('user_id', userId)
              .eq('status', 'active')
              .eq('is_primary', true)
              .single();
            if (roleData?.tenant_id) {
              const { data: tenantData } = await supabase
                .from('tenants')
                .select('default_locale')
                .eq('id', roleData.tenant_id)
                .single();
              if (
                tenantData?.default_locale &&
                locales.includes(tenantData.default_locale as Locale)
              ) {
                await setUserLocale(tenantData.default_locale as Locale);
              }
            }
          } catch (localeError) {
            console.warn('Failed to load tenant locale:', localeError);
          }
        })();
      }

      // Successful login.
      // SECURITY: Clear ALL localStorage/sessionStorage to prevent tenant_id leaks
      localStorage.clear();
      sessionStorage.clear();

      // Hard redirect — short paint delay (was 1000ms; locale preload no longer blocks).
      setTimeout(() => {
        const htmlLang = document.documentElement.lang || '';
        const localePrefix = htmlLang && htmlLang !== 'de' ? `/${htmlLang}` : '';
        const basePath = `${localePrefix}/admin`;
        const target = tenantId ? `${basePath}?tenantId=${tenantId}` : basePath;
        window.location.href = target;
      }, 300);
    },
  });

  return (
    <div className="space-y-4">
      {/* Google OAuth — primary alternative, single tap. Matches the
          chrome of the RegisterForm Google button so users see the
          same surface on both ends of the auth funnel. */}
      <button
        type="button"
        onClick={handleGoogleLogin}
        disabled={oauthLoading || isLoading || magicLinkBusy}
        className="group flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-colors hover:border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:opacity-60"
      >
        {oauthLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
        ) : (
          <GoogleIcon />
        )}
        {tAuth('googleSignIn')}
      </button>

      {oauthError && <ErrorAlert error={oauthError} onDismiss={() => setOauthError(null)} />}

      <div className="flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-gray-400">
        <div className="h-px flex-1 bg-gray-200" />
        {tAuth('or')}
        <div className="h-px flex-1 bg-gray-200" />
      </div>

      {/* Mode tabs — same chrome as the segmented controls in
          /admin/subscription, keeps the brand consistent. */}
      <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
        <button
          type="button"
          onClick={() => setMode('password')}
          className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
            mode === 'password'
              ? 'bg-white text-gray-950 shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {tAuth('passwordLabel')}
        </button>
        <button
          type="button"
          onClick={() => {
            setMode('magic-link');
            setMagicLinkSent(false);
            setMagicLinkError(null);
          }}
          className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
            mode === 'magic-link'
              ? 'bg-white text-gray-950 shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {tAuth('magicLinkTab')}
        </button>
      </div>

      {mode === 'password' ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <ErrorAlert error={error} onDismiss={clearError} />

          <div className="space-y-1.5">
            <Label htmlFor="email">{tAuth('emailLabel')}</Label>
            <Input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={tAuth('emailPlaceholder')}
              autoComplete="email"
              required
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between">
              <Label htmlFor="password">{tAuth('passwordLabel')}</Label>
              <Link
                href={`/admin/forgot-password${tenantId ? `?tenantId=${tenantId}` : ''}`}
                className="text-xs font-medium text-blue-600 underline-offset-2 hover:underline"
              >
                {tAuth('forgotPassword')}
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={tAuth('passwordPlaceholder')}
              autoComplete="current-password"
              required
            />
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {tAuth('submitting')}
              </>
            ) : (
              tAuth('submit')
            )}
          </Button>
        </form>
      ) : magicLinkSent ? (
        <div className="space-y-3">
          <AuthNotice tone="success">{tAuth('magicLinkSentNotice')}</AuthNotice>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              setMagicLinkSent(false);
              setMagicLinkEmail('');
            }}
          >
            {tAuth('magicLinkResend')}
          </Button>
        </div>
      ) : (
        <form
          onSubmit={handleMagicLink}
          className="space-y-4"
          onChange={() => magicLinkNotRegistered && setMagicLinkNotRegistered(false)}
        >
          {magicLinkError && (
            <AuthNotice tone="error">{magicLinkError}</AuthNotice>
          )}

          {magicLinkNotRegistered && (
            <AuthNotice tone="error">
              {tAuth('magicLinkNotRegistered')}{' '}
              <Link
                href={`/register?email=${encodeURIComponent(magicLinkEmail)}`}
                className="font-semibold underline underline-offset-2"
              >
                {tAuth('registerNow')}
              </Link>
            </AuthNotice>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="magic-email">{tAuth('emailLabel')}</Label>
            <Input
              id="magic-email"
              type="email"
              value={magicLinkEmail}
              onChange={(e) => setMagicLinkEmail(e.target.value)}
              placeholder={tAuth('emailPlaceholder')}
              autoComplete="email"
              required
            />
            <p className="text-xs text-gray-500">{tAuth('magicLinkHint')}</p>
          </div>

          <Button type="submit" className="w-full" disabled={magicLinkBusy}>
            {magicLinkBusy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {tAuth('magicLinkSending')}
              </>
            ) : (
              tAuth('magicLinkSubmit')
            )}
          </Button>
        </form>
      )}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M16.51 8.18c0-.56-.05-1.1-.14-1.62H9v3.07h4.21c-.18.99-.74 1.83-1.58 2.39v1.98h2.55c1.49-1.37 2.34-3.4 2.34-5.82z" />
      <path fill="#34A853" d="M9 17c2.13 0 3.92-.71 5.22-1.92l-2.55-1.98c-.71.48-1.61.77-2.67.77-2.05 0-3.79-1.39-4.41-3.25H1.96v2.04A8 8 0 0 0 9 17z" />
      <path fill="#FBBC04" d="M4.59 10.62c-.16-.48-.25-.99-.25-1.52s.09-1.04.25-1.52V5.54H1.96A8 8 0 0 0 1 9c0 1.29.31 2.51.96 3.46l2.63-2.04z" />
      <path fill="#EA4335" d="M9 4.23c1.16 0 2.2.4 3.02 1.18l2.26-2.26C13.92 1.86 12.13 1 9 1A8 8 0 0 0 1.96 5.54l2.63 2.04C5.21 5.72 6.95 4.23 9 4.23z" />
    </svg>
  );
}
